import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db } from '../services/firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, serverTimestamp
} from 'firebase/firestore';
import {
  MapContainer, TileLayer, Marker, Polyline, CircleMarker, Tooltip, useMap, useMapEvents
} from 'react-leaflet';
import L from 'leaflet';
import {
  Save, Loader2, AlertCircle, Plus, Search, Trash2, Check, MapPin, Navigation, Milestone, X
} from './Icons';
import { Maximize2, Minimize2, UserCircle } from 'lucide-react';
import './CollectionEditor.css';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ─── Location Editor ─────────────────────────────────────────────────────────

const DEFAULT_LOCATION = {
  name: '',
  location_name: '',
  city: '',
  state: '',
  coordinates: { lat: 39.8283, lon: -98.5795, zoom: 5 },
  ops_time_start: '',
  ops_time_end: '',
  parking: [],
  images: [],
  logo: '',
  admin_app: { status: 'free', stripe_customer_id: '', subscription_id: '', current_period_end: '' },
  revenue_share_perc: 0.09,
};

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function MapZoomTracker({ onZoomChange }) {
  const map = useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
  });
  return null;
}

function MapResizeHandler({ expanded }) {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 300);
    return () => clearTimeout(timer);
  }, [expanded, map]);
  return null;
}

function FlyToPosition({ lat, lon, zoom }) {
  const map = useMap();
  const prevRef = useRef({ lat, lon, zoom });
  useEffect(() => {
    const prev = prevRef.current;
    if (prev.lat !== lat || prev.lon !== lon || prev.zoom !== zoom) {
      map.flyTo([lat, lon], zoom, { duration: 0.6 });
      prevRef.current = { lat, lon, zoom };
    }
  }, [lat, lon, zoom, map]);
  return null;
}

function LocationEditor({ locationId, data, onChange, locations, isNew, customId, onCustomIdChange }) {
  const [parkingInput, setParkingInput] = useState('');
  const [imageInput, setImageInput] = useState('');
  const [mapExpanded, setMapExpanded] = useState(false);

  const set = (field, value) => onChange({ ...data, [field]: value });
  const setCoord = (field, value) => onChange({ ...data, coordinates: { ...data.coordinates, [field]: value } });
  const setAdmin = (field, value) => onChange({ ...data, admin_app: { ...data.admin_app, [field]: value } });

  const handleMapClick = useCallback((latlng) => {
    onChange(prev => ({
      ...prev,
      coordinates: { ...prev.coordinates, lat: latlng.lat, lon: latlng.lng }
    }));
  }, [onChange]);

  const handleZoomChange = useCallback((zoom) => {
    onChange(prev => ({
      ...prev,
      coordinates: { ...prev.coordinates, zoom }
    }));
  }, [onChange]);

  const addParking = () => {
    if (!parkingInput.trim()) return;
    set('parking', [...(data.parking || []), parkingInput.trim()]);
    setParkingInput('');
  };

  const removeParking = (idx) => {
    set('parking', (data.parking || []).filter((_, i) => i !== idx));
  };

  const addImage = () => {
    if (!imageInput.trim()) return;
    set('images', [...(data.images || []), imageInput.trim()]);
    setImageInput('');
  };

  const removeImage = (idx) => {
    set('images', (data.images || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="ce-form">
      <div className="ce-form-grid">
        {/* Identity */}
        <div className="ce-fieldset">
          <div className="ce-section-title">Identity</div>
          <div className="ce-field">
            <label>Document ID</label>
            {isNew ? (
              <>
                <input type="text" value={customId} onChange={e => onCustomIdChange(e.target.value)} className="ce-input" placeholder="Leave blank for auto-generated ID" />
                <span className="ce-hint">Optional — leave empty to let Firebase assign an ID automatically.</span>
              </>
            ) : (
              <>
                <input type="text" value={locationId} disabled className="ce-input ce-input-disabled" />
                <span className="ce-hint">Cannot be changed after creation.</span>
              </>
            )}
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>name</label>
              <input type="text" value={data.name || ''} onChange={e => set('name', e.target.value)} className="ce-input" placeholder="e.g. Oz Adventure Park" />
            </div>
            <div className="ce-field">
              <label>location_name</label>
              <input type="text" value={data.location_name || ''} onChange={e => set('location_name', e.target.value)} className="ce-input" placeholder="e.g. Oz Adventure Park" />
            </div>
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>city</label>
              <input type="text" value={data.city || ''} onChange={e => set('city', e.target.value)} className="ce-input" placeholder="e.g. Beech Mountain" />
            </div>
            <div className="ce-field">
              <label>state</label>
              <input type="text" value={data.state || ''} onChange={e => set('state', e.target.value)} className="ce-input" placeholder="e.g. NC" />
            </div>
          </div>
        </div>

        {/* Coordinates Map */}
        {mapExpanded && <div className="ce-map-overlay-backdrop" onClick={() => setMapExpanded(false)} />}
        <div className={`ce-fieldset ce-fieldset-map ${mapExpanded ? 'ce-map-expanded' : ''}`}>
          <div className="ce-section-title">
            Coordinates (click map to set pin)
            <button className="ce-map-expand-btn" onClick={() => setMapExpanded(v => !v)} title={mapExpanded ? 'Collapse map' : 'Expand map'}>
              {mapExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
          <div className="ce-map-wrapper">
            <MapContainer
              center={[data.coordinates?.lat || 39.8283, data.coordinates?.lon || -98.5795]}
              zoom={data.coordinates?.zoom || 5}
              style={{ height: '100%', width: '100%' }}
              maxZoom={22}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={22}
                maxNativeZoom={19}
              />
              <MapResizeHandler expanded={mapExpanded} />
              <MapClickHandler onMapClick={handleMapClick} />
              <MapZoomTracker onZoomChange={handleZoomChange} />
              <FlyToPosition lat={data.coordinates?.lat || 39.8283} lon={data.coordinates?.lon || -98.5795} zoom={data.coordinates?.zoom || 5} />
              {data.coordinates?.lat && data.coordinates?.lon && (
                <Marker position={[data.coordinates.lat, data.coordinates.lon]} />
              )}
            </MapContainer>
          </div>
          <div className="ce-coord-row">
            <div className="ce-field">
              <label>lat</label>
              <input type="number" step="any" value={data.coordinates?.lat ?? ''} onChange={e => setCoord('lat', parseFloat(e.target.value) || 0)} className="ce-input ce-input-sm" />
            </div>
            <div className="ce-field">
              <label>lon</label>
              <input type="number" step="any" value={data.coordinates?.lon ?? ''} onChange={e => setCoord('lon', parseFloat(e.target.value) || 0)} className="ce-input ce-input-sm" />
            </div>
            <div className="ce-field">
              <label>zoom</label>
              <input type="number" step="1" min="1" max="22" value={data.coordinates?.zoom ?? 5} onChange={e => setCoord('zoom', parseInt(e.target.value) || 5)} className="ce-input ce-input-sm" />
            </div>
          </div>
        </div>

        {/* Operations */}
        <div className="ce-fieldset">
          <div className="ce-section-title">Operations</div>
          <div className="ce-row">
            <div className="ce-field">
              <label>ops_time_start</label>
              <input type="text" value={data.ops_time_start || ''} onChange={e => set('ops_time_start', e.target.value)} className="ce-input" placeholder="e.g. 09:00" />
            </div>
            <div className="ce-field">
              <label>ops_time_end</label>
              <input type="text" value={data.ops_time_end || ''} onChange={e => set('ops_time_end', e.target.value)} className="ce-input" placeholder="e.g. 17:00" />
            </div>
          </div>
        </div>

        {/* Admin App */}
        <div className="ce-fieldset">
          <div className="ce-section-title">admin_app</div>
          <div className="ce-row">
            <div className="ce-field">
              <label>status</label>
              <select value={data.admin_app?.status || 'free'} onChange={e => setAdmin('status', e.target.value)} className="ce-input">
                <option value="free">free</option>
                <option value="active">active</option>
                <option value="trialing">trialing</option>
                <option value="past_due">past_due</option>
                <option value="canceled">canceled</option>
              </select>
            </div>
            <div className="ce-field">
              <label>revenue_share_perc</label>
              <input type="number" step="0.01" min="0" max="1" value={data.revenue_share_perc ?? 0.09} onChange={e => set('revenue_share_perc', parseFloat(e.target.value) || 0)} className="ce-input" />
              <span className="ce-hint">{((data.revenue_share_perc || 0) * 100).toFixed(1)}%</span>
            </div>
          </div>
          <div className="ce-field">
            <label>stripe_customer_id</label>
            <input type="text" value={data.admin_app?.stripe_customer_id || ''} onChange={e => setAdmin('stripe_customer_id', e.target.value)} className="ce-input" placeholder="cus_..." />
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>subscription_id</label>
              <input type="text" value={data.admin_app?.subscription_id || ''} onChange={e => setAdmin('subscription_id', e.target.value)} className="ce-input" placeholder="sub_..." />
            </div>
            <div className="ce-field">
              <label>current_period_end</label>
              <input type="text" value={data.admin_app?.current_period_end || ''} onChange={e => setAdmin('current_period_end', e.target.value)} className="ce-input" placeholder="ISO date or timestamp" />
            </div>
          </div>
        </div>

        {/* Media */}
        <div className="ce-fieldset">
          <div className="ce-section-title">Media &amp; Parking</div>
          <div className="ce-field">
            <label>logo</label>
            <input type="text" value={data.logo || ''} onChange={e => set('logo', e.target.value)} className="ce-input" placeholder="URL to logo image" />
          </div>
          <div className="ce-field">
            <label>images</label>
            <div className="ce-list">
              {(data.images || []).map((img, i) => (
                <div key={i} className="ce-list-item">
                  <span className="ce-list-text">{img}</span>
                  <button className="ce-list-remove" onClick={() => removeImage(i)}><Trash2 size={12} /></button>
                </div>
              ))}
              <div className="ce-list-add">
                <input type="text" value={imageInput} onChange={e => setImageInput(e.target.value)} className="ce-input" placeholder="Image URL" onKeyDown={e => e.key === 'Enter' && addImage()} />
                <button className="ce-btn-icon" onClick={addImage}><Plus size={14} /></button>
              </div>
            </div>
          </div>
          <div className="ce-field">
            <label>parking</label>
            <div className="ce-list">
              {(data.parking || []).map((p, i) => (
                <div key={i} className="ce-list-item">
                  <span className="ce-list-text">{typeof p === 'object' ? JSON.stringify(p) : p}</span>
                  <button className="ce-list-remove" onClick={() => removeParking(i)}><Trash2 size={12} /></button>
                </div>
              ))}
              <div className="ce-list-add">
                <input type="text" value={parkingInput} onChange={e => setParkingInput(e.target.value)} className="ce-input" placeholder="Parking info" onKeyDown={e => e.key === 'Enter' && addParking()} />
                <button className="ce-btn-icon" onClick={addParking}><Plus size={14} /></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Gate Editor ──────────────────────────────────────────────────────────────

const DEFAULT_GATE = {
  coordinates: [{ latitude: 0, longitude: 0 }, { latitude: 0, longitude: 0 }],
  trigger_polarity: true,
  location_id: '',
  location_name: '',
  trail_name: '',
  trail_id: '',
  placement: '',
};

function GateMapEditor({ coordinates, onChange }) {
  const coord0 = coordinates?.[0] || { latitude: 0, longitude: 0 };
  const coord1 = coordinates?.[1] || { latitude: 0, longitude: 0 };
  const [activePoint, setActivePoint] = useState(0);
  const [mapExpanded, setMapExpanded] = useState(false);

  const center = [
    (coord0.latitude + coord1.latitude) / 2 || 39.8283,
    (coord0.longitude + coord1.longitude) / 2 || -98.5795,
  ];

  const icon0 = L.divIcon({
    className: 'ce-gate-marker ce-gate-marker-0',
    html: '<div class="ce-gate-dot" style="background:#2196F3">A</div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
  const icon1 = L.divIcon({
    className: 'ce-gate-marker ce-gate-marker-1',
    html: '<div class="ce-gate-dot" style="background:#f44336">B</div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  const handleMapClick = useCallback((latlng) => {
    const newCoords = [...(coordinates || [{ latitude: 0, longitude: 0 }, { latitude: 0, longitude: 0 }])];
    newCoords[activePoint] = { latitude: latlng.lat, longitude: latlng.lng };
    onChange(newCoords);
    setActivePoint(p => (p === 0 ? 1 : 0));
  }, [coordinates, activePoint, onChange]);

  const hasValidCoords = coord0.latitude !== 0 || coord0.longitude !== 0;
  const zoom = hasValidCoords ? 17 : 5;

  return (
    <div className={`ce-gate-map-section ${mapExpanded ? 'ce-map-expanded' : ''}`}>
      {mapExpanded && <div className="ce-map-overlay-backdrop" onClick={() => setMapExpanded(false)} />}
      <div className="ce-gate-map-header">
        <div className="ce-gate-point-toggle">
          <button className={`ce-toggle-btn ${activePoint === 0 ? 'active point-a' : ''}`} onClick={() => setActivePoint(0)}>
            Setting Point A
          </button>
          <button className={`ce-toggle-btn ${activePoint === 1 ? 'active point-b' : ''}`} onClick={() => setActivePoint(1)}>
            Setting Point B
          </button>
        </div>
        <button className="ce-map-expand-btn" onClick={() => setMapExpanded(v => !v)} title={mapExpanded ? 'Collapse map' : 'Expand map'}>
          {mapExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
      </div>
      <div className="ce-map-wrapper">
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          maxZoom={22}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={22}
            maxNativeZoom={19}
          />
          <MapResizeHandler expanded={mapExpanded} />
          <MapClickHandler onMapClick={handleMapClick} />
          {(coord0.latitude !== 0 || coord0.longitude !== 0) && (
            <Marker position={[coord0.latitude, coord0.longitude]} icon={icon0} />
          )}
          {(coord1.latitude !== 0 || coord1.longitude !== 0) && (
            <Marker position={[coord1.latitude, coord1.longitude]} icon={icon1} />
          )}
        </MapContainer>
      </div>
      <div className="ce-coord-row">
        <div className="ce-field">
          <label>A lat</label>
          <input type="number" step="any" value={coord0.latitude} onChange={e => {
            const newCoords = [...coordinates]; newCoords[0] = { ...newCoords[0], latitude: parseFloat(e.target.value) || 0 }; onChange(newCoords);
          }} className="ce-input ce-input-sm" />
        </div>
        <div className="ce-field">
          <label>A lon</label>
          <input type="number" step="any" value={coord0.longitude} onChange={e => {
            const newCoords = [...coordinates]; newCoords[0] = { ...newCoords[0], longitude: parseFloat(e.target.value) || 0 }; onChange(newCoords);
          }} className="ce-input ce-input-sm" />
        </div>
        <div className="ce-field">
          <label>B lat</label>
          <input type="number" step="any" value={coord1.latitude} onChange={e => {
            const newCoords = [...coordinates]; newCoords[1] = { ...newCoords[1], latitude: parseFloat(e.target.value) || 0 }; onChange(newCoords);
          }} className="ce-input ce-input-sm" />
        </div>
        <div className="ce-field">
          <label>B lon</label>
          <input type="number" step="any" value={coord1.longitude} onChange={e => {
            const newCoords = [...coordinates]; newCoords[1] = { ...newCoords[1], longitude: parseFloat(e.target.value) || 0 }; onChange(newCoords);
          }} className="ce-input ce-input-sm" />
        </div>
      </div>
    </div>
  );
}

function GateEditor({ gateId, data, onChange, locations, isNew, customId, onCustomIdChange }) {
  const set = (field, value) => onChange({ ...data, [field]: value });

  const sortedLocations = useMemo(() =>
    [...locations].sort((a, b) => (a.data.name || a.data.location_name || '').localeCompare(b.data.name || b.data.location_name || '')),
    [locations]
  );

  const handleLocationChange = (locId) => {
    const loc = locations.find(l => l.id === locId);
    if (loc) {
      onChange({ ...data, location_id: locId, location_name: loc.data.name || loc.data.location_name || '' });
    } else {
      onChange({ ...data, location_id: locId, location_name: '' });
    }
  };

  return (
    <div className="ce-form">
      <div className="ce-form-grid">
        <div className="ce-fieldset">
          <div className="ce-section-title">Identity</div>
          <div className="ce-field">
            <label>Document ID</label>
            {isNew ? (
              <>
                <input type="text" value={customId} onChange={e => onCustomIdChange(e.target.value)} className="ce-input" placeholder="Leave blank for auto-generated ID" />
                <span className="ce-hint">Optional — leave empty to let Firebase assign an ID automatically.</span>
              </>
            ) : (
              <input type="text" value={gateId} disabled className="ce-input ce-input-disabled" />
            )}
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>location_id</label>
              <select value={data.location_id || ''} onChange={e => handleLocationChange(e.target.value)} className="ce-input">
                <option value="">— select location —</option>
                {sortedLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.data.name || loc.data.location_name || loc.id}</option>
                ))}
              </select>
            </div>
            <div className="ce-field">
              <label>location_name</label>
              <input type="text" value={data.location_name || ''} onChange={e => set('location_name', e.target.value)} className="ce-input" placeholder="Auto-set from location" />
            </div>
          </div>
        </div>

        <div className="ce-fieldset">
          <div className="ce-section-title">Trail &amp; Placement</div>
          <div className="ce-row">
            <div className="ce-field">
              <label>trail_name</label>
              <input type="text" value={data.trail_name || ''} onChange={e => set('trail_name', e.target.value)} className="ce-input" placeholder='e.g. "Catapult"' />
            </div>
            <div className="ce-field">
              <label>placement</label>
              <input type="text" value={data.placement || ''} onChange={e => set('placement', e.target.value)} className="ce-input" placeholder='e.g. "Top" or "Bottom"' />
            </div>
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>trail_id</label>
              <input type="text" value={data.trail_id || ''} onChange={e => set('trail_id', e.target.value)} className="ce-input" placeholder="trails doc ID (optional)" />
              <span className="ce-hint">Link to a trail document if one exists</span>
            </div>
          </div>
          <div className="ce-field" style={{marginTop: 8}}>
            <label>trigger_polarity</label>
            <div className="ce-toggle-row">
              <button
                className={`ce-toggle-btn ${data.trigger_polarity !== false ? 'active' : ''}`}
                onClick={() => set('trigger_polarity', true)}
              >True</button>
              <button
                className={`ce-toggle-btn ${data.trigger_polarity === false ? 'active' : ''}`}
                onClick={() => set('trigger_polarity', false)}
              >False</button>
            </div>
          </div>
        </div>

        <div className="ce-fieldset ce-fieldset-map">
          <div className="ce-section-title">Coordinates (click map to set A, then B)</div>
          <GateMapEditor
            coordinates={data.coordinates || DEFAULT_GATE.coordinates}
            onChange={(coords) => set('coordinates', coords)}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Trail Editor ─────────────────────────────────────────────────────────────

const DIFFICULTY_OPTIONS = [
  { value: 'Beginner', coded: 'green', label: 'Beginner', color: '#4CAF50' },
  { value: 'Intermediate', coded: 'blue', label: 'Intermediate', color: '#2196F3' },
  { value: 'Advanced', coded: 'black', label: 'Advanced', color: '#333' },
  { value: 'Expert', coded: 'double_black', label: 'Expert', color: '#111' },
];

const DEFAULT_TRAIL = {
  trail_name: '',
  name: '',
  location_id: '',
  difficulty: 'Beginner',
};

function TrailEditor({ trailId, data, onChange, locations, gates, isNew, customId, onCustomIdChange }) {
  const set = (field, value) => onChange({ ...data, [field]: value });

  const handleLocationChange = (locId) => {
    onChange({
      ...data,
      location_id: locId,
    });
  };

  const handleNameChange = (value) => {
    onChange({ ...data, trail_name: value, name: value });
  };

  const diffOption = DIFFICULTY_OPTIONS.find(d => d.value === data.difficulty) || DIFFICULTY_OPTIONS[0];
  const codedValue = diffOption.coded;

  const trailGates = gates.filter(g =>
    g.data.trail_name && data.trail_name &&
    g.data.trail_name.toLowerCase() === data.trail_name.toLowerCase()
  );

  return (
    <div className="ce-form">
      <div className="ce-form-grid">
        <div className="ce-fieldset">
          <div className="ce-section-title">Identity</div>
          <div className="ce-field">
            <label>Document ID</label>
            {isNew ? (
              <>
                <input type="text" value={customId} onChange={e => onCustomIdChange(e.target.value)} className="ce-input" placeholder="Leave blank for auto-generated ID" />
                <span className="ce-hint">Optional — leave empty to let Firebase assign an ID automatically.</span>
              </>
            ) : (
              <input type="text" value={trailId} disabled className="ce-input ce-input-disabled" />
            )}
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>trail_name / name</label>
              <input type="text" value={data.trail_name || ''} onChange={e => handleNameChange(e.target.value)} className="ce-input" placeholder='e.g. "Catapult"' />
              <span className="ce-hint">Sets both trail_name and name fields</span>
            </div>
            <div className="ce-field">
              <label>location_id</label>
              <select value={data.location_id || ''} onChange={e => handleLocationChange(e.target.value)} className="ce-input">
                <option value="">— select location —</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.data.name || loc.data.location_name || loc.id}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="ce-fieldset">
          <div className="ce-section-title">Difficulty</div>
          <div className="ce-field">
            <label>difficulty</label>
            <div className="ce-difficulty-grid">
              {DIFFICULTY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`ce-difficulty-btn ${data.difficulty === opt.value ? 'active' : ''}`}
                  onClick={() => set('difficulty', opt.value)}
                  style={{
                    '--diff-color': opt.color,
                    '--diff-bg': data.difficulty === opt.value ? `${opt.color}22` : 'transparent',
                  }}
                >
                  <span className="ce-difficulty-diamond" style={{ background: opt.color }} />
                  <span className="ce-difficulty-label">{opt.label}</span>
                  <span className="ce-difficulty-code">{opt.coded}</span>
                </button>
              ))}
            </div>
            <span className="ce-hint">
              Stored as free text: <code>"{data.difficulty || 'Beginner'}"</code> — app normalizes to coded enum: <code>"{codedValue}"</code>
            </span>
          </div>
        </div>

        <div className="ce-fieldset">
          <div className="ce-section-title">Associated Gates</div>
          <div className="ce-field">
            {trailGates.length > 0 ? (
              <div className="ce-list">
                {trailGates.map(g => (
                  <div key={g.id} className="ce-list-item">
                    <span className="ce-list-text">
                      {g.id}
                      {g.data.placement ? ` — ${g.data.placement}` : ''}
                      {g.data.location_id ? ` (${g.data.location_id})` : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="ce-hint">
                {data.trail_name
                  ? `No gates found with trail_name "${data.trail_name}". Assign trail_name on gates via the Gates tab.`
                  : 'Set a trail name above to see matching gates.'}
              </span>
            )}
            <span className="ce-hint" style={{ marginTop: 4 }}>
              Gate-trail linking is managed on the Gates tab via the trail_name field.
            </span>
          </div>
        </div>

        <div className="ce-fieldset">
          <div className="ce-section-title">Analytics (read-only)</div>
          <div className="ce-field">
            <span className="ce-hint">
              Analytics fields (total_runs, clip_generation_rate, etc.) are not yet present in Firestore.
              These should be derived from gate_crossings via a Cloud Function rollup.
              The Trail Popularity chart is blocked on this data.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Admin User Editor ────────────────────────────────────────────────────────

const DEFAULT_LOCATION_ACCESS = {
  location_id: '',
  role: 'viewer',
  status: 'active',
  created_at: '',
};

const DEFAULT_ADMIN_USER = {
  clerk_user_id: '',
  email: '',
  name: '',
  status: 'pending',
  default_location_id: '',
  locations: [],
  created_at: '',
  updated_at: '',
};

const ADMIN_USER_STATUS_OPTIONS = ['active', 'inactive', 'pending'];
const LOCATION_ROLE_OPTIONS = ['owner', 'admin', 'viewer'];
const LOCATION_ACCESS_STATUS_OPTIONS = ['active', 'inactive', 'pending'];

function parseFirestoreTimestamp(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts._seconds !== undefined) return new Date(ts._seconds * 1000);
  if (typeof ts === 'string') return new Date(ts);
  if (ts instanceof Date) return ts;
  return null;
}

function formatTimestampForDisplay(ts) {
  const date = parseFirestoreTimestamp(ts);
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function AdminUserEditor({ userId, data, onChange, locations, isNew, customId, onCustomIdChange }) {
  const set = (field, value) => onChange({ ...data, [field]: value });

  const sortedLocations = useMemo(() =>
    [...locations].sort((a, b) =>
      (a.data.name || a.data.location_name || '').localeCompare(b.data.name || b.data.location_name || '')
    ),
    [locations]
  );

  const updateLocationAccess = (index, field, value) => {
    const next = [...(data.locations || [])];
    next[index] = { ...next[index], [field]: value };
    onChange({ ...data, locations: next });
  };

  const addLocationAccess = () => {
    onChange({
      ...data,
      locations: [...(data.locations || []), { ...DEFAULT_LOCATION_ACCESS }],
    });
  };

  const removeLocationAccess = (index) => {
    onChange({
      ...data,
      locations: (data.locations || []).filter((_, i) => i !== index),
    });
  };

  return (
    <div className="ce-form">
      <div className="ce-form-grid">
        <div className="ce-fieldset">
          <div className="ce-section-title">Identity</div>
          <div className="ce-field">
            <label>clerk_user_id (Document ID)</label>
            {isNew ? (
              <>
                <input
                  type="text"
                  value={customId}
                  onChange={e => onCustomIdChange(e.target.value)}
                  className="ce-input"
                  placeholder="Clerk user ID, e.g. user_2abc..."
                />
                <span className="ce-hint">Required — must match the Clerk user ID.</span>
              </>
            ) : (
              <>
                <input type="text" value={userId} disabled className="ce-input ce-input-disabled" />
                <span className="ce-hint">Document ID matches Clerk user ID.</span>
              </>
            )}
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>email</label>
              <input
                type="email"
                value={data.email || ''}
                onChange={e => set('email', e.target.value)}
                className="ce-input"
                placeholder="owner@park.com"
              />
            </div>
            <div className="ce-field">
              <label>name</label>
              <input
                type="text"
                value={data.name || ''}
                onChange={e => set('name', e.target.value)}
                className="ce-input"
                placeholder="Display name from Clerk"
              />
            </div>
          </div>
          <div className="ce-row">
            <div className="ce-field">
              <label>status</label>
              <select value={data.status || 'pending'} onChange={e => set('status', e.target.value)} className="ce-input">
                {ADMIN_USER_STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <span className="ce-hint">Set to active when the owner is provisioned.</span>
            </div>
            <div className="ce-field">
              <label>default_location_id</label>
              <select
                value={data.default_location_id || ''}
                onChange={e => set('default_location_id', e.target.value)}
                className="ce-input"
              >
                <option value="">— none —</option>
                {sortedLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.data.name || loc.data.location_name || loc.id}
                  </option>
                ))}
              </select>
              <span className="ce-hint">Park loaded first after sign-in.</span>
            </div>
          </div>
        </div>

        <div className="ce-fieldset">
          <div className="ce-section-title">Location Access</div>
          <span className="ce-hint" style={{ marginBottom: 8, display: 'block' }}>
            One entry per park this user can access. Roles gate configuration, not analytics.
          </span>
          {(data.locations || []).length === 0 ? (
            <div className="ce-empty-inline">No location access entries yet.</div>
          ) : (
            <div className="ce-access-table">
              <div className="ce-access-header">
                <span>Location</span>
                <span>Role</span>
                <span>Status</span>
                <span>Added</span>
                <span></span>
              </div>
              {(data.locations || []).map((entry, index) => (
                <div key={index} className="ce-access-row">
                  <select
                    value={entry.location_id || ''}
                    onChange={e => updateLocationAccess(index, 'location_id', e.target.value)}
                    className="ce-input ce-input-sm"
                  >
                    <option value="">— select —</option>
                    {sortedLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.data.name || loc.data.location_name || loc.id}
                      </option>
                    ))}
                  </select>
                  <select
                    value={entry.role || 'viewer'}
                    onChange={e => updateLocationAccess(index, 'role', e.target.value)}
                    className="ce-input ce-input-sm"
                  >
                    {LOCATION_ROLE_OPTIONS.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <select
                    value={entry.status || 'active'}
                    onChange={e => updateLocationAccess(index, 'status', e.target.value)}
                    className="ce-input ce-input-sm"
                  >
                    {LOCATION_ACCESS_STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <span className="ce-access-added">
                    {entry.created_at ? formatTimestampForDisplay(entry.created_at) : 'New'}
                  </span>
                  <button
                    type="button"
                    className="ce-list-remove"
                    onClick={() => removeLocationAccess(index)}
                    title="Remove access"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button type="button" className="ce-btn-sm" onClick={addLocationAccess} style={{ marginTop: 8 }}>
            <Plus size={14} /> Add location access
          </button>
        </div>

        {!isNew && (
          <div className="ce-fieldset">
            <div className="ce-section-title">Audit (read-only)</div>
            <div className="ce-row">
              <div className="ce-field">
                <label>created_at</label>
                <input type="text" value={formatTimestampForDisplay(data.created_at)} disabled className="ce-input ce-input-disabled" />
              </div>
              <div className="ce-field">
                <label>updated_at</label>
                <input type="text" value={formatTimestampForDisplay(data.updated_at)} disabled className="ce-input ce-input-disabled" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Gate Map Browser ─────────────────────────────────────────────────────────

function FlyToBounds({ bounds }) {
  const map = useMap();
  const prevRef = useRef(null);
  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    const key = JSON.stringify(bounds);
    if (prevRef.current === key) return;
    prevRef.current = key;
    map.flyToBounds(bounds, { padding: [50, 50], duration: 0.6, maxZoom: 17 });
  }, [bounds, map]);
  return null;
}

function GateMapBrowser({ gates, locations, selectedGateId, onSelectGate, locationFilter }) {
  const [hoveredGate, setHoveredGate] = useState(null);

  const gateLines = useMemo(() => {
    const filtered = locationFilter
      ? gates.filter(g => g.data.location_id === locationFilter)
      : gates;

    return filtered.map(gate => {
      const coords = gate.data.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return null;
      const c0 = coords[0];
      const c1 = coords[1];
      if (!c0 || !c1) return null;
      const lat0 = c0.latitude || 0;
      const lon0 = c0.longitude || 0;
      const lat1 = c1.latitude || 0;
      const lon1 = c1.longitude || 0;
      if (lat0 === 0 && lon0 === 0 && lat1 === 0 && lon1 === 0) return null;

      return {
        id: gate.id,
        trail_name: gate.data.trail_name || '',
        placement: gate.data.placement || '',
        location_name: gate.data.location_name || '',
        location_id: gate.data.location_id || '',
        trigger_polarity: gate.data.trigger_polarity,
        positions: [[lat0, lon0], [lat1, lon1]],
        center: [(lat0 + lat1) / 2, (lon0 + lon1) / 2],
      };
    }).filter(Boolean);
  }, [gates, locationFilter]);

  const mapBounds = useMemo(() => {
    if (gateLines.length === 0) return null;
    const allPts = [];
    gateLines.forEach(g => { allPts.push(g.positions[0]); allPts.push(g.positions[1]); });
    return allPts;
  }, [gateLines]);

  const initialCenter = useMemo(() => {
    if (gateLines.length > 0) {
      const lat = gateLines.reduce((s, g) => s + g.center[0], 0) / gateLines.length;
      const lon = gateLines.reduce((s, g) => s + g.center[1], 0) / gateLines.length;
      return [lat, lon];
    }
    return [39.8283, -98.5795];
  }, [gateLines]);

  const missingFieldCount = useMemo(() => {
    const filtered = locationFilter
      ? gates.filter(g => g.data.location_id === locationFilter)
      : gates;
    return filtered.filter(g => !g.data.trail_name || !g.data.placement || !g.data.location_id).length;
  }, [gates, locationFilter]);

  return (
    <div className="ce-gate-browser">
      <div className="ce-gate-browser-stats">
        <span>{gateLines.length} gate{gateLines.length !== 1 ? 's' : ''} on map</span>
        {missingFieldCount > 0 && (
          <span className="ce-missing-badge">{missingFieldCount} missing fields</span>
        )}
      </div>
      <div className="ce-gate-browser-map">
        <MapContainer
          center={initialCenter}
          zoom={gateLines.length > 0 ? 15 : 5}
          maxZoom={22}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={22}
            maxNativeZoom={19}
          />
          {mapBounds && <FlyToBounds bounds={mapBounds} />}

          {gateLines.map(gate => {
            const isSelected = gate.id === selectedGateId;
            const isHovered = gate.id === hoveredGate;
            const hasMissing = !gate.trail_name || !gate.placement || !gate.location_id;

            let color = hasMissing ? '#ff9800' : '#4CAF50';
            if (isSelected) color = '#2196F3';
            else if (isHovered) color = '#fc5f21';

            const weight = isSelected ? 6 : isHovered ? 5 : 3;

            return (
              <React.Fragment key={gate.id}>
                <Polyline
                  positions={gate.positions}
                  pathOptions={{ color, weight, opacity: 0.9 }}
                  eventHandlers={{
                    click: () => onSelectGate(gate.id),
                    mouseover: () => setHoveredGate(gate.id),
                    mouseout: () => setHoveredGate(null),
                  }}
                >
                  <Tooltip sticky>
                    <div className="ce-gate-tooltip">
                      <strong>{gate.id}</strong>
                      {gate.trail_name && <div>Trail: {gate.trail_name}</div>}
                      {gate.placement && <div>Placement: {gate.placement}</div>}
                      {gate.location_name && <div>Location: {gate.location_name}</div>}
                      {hasMissing && <div className="ce-tooltip-missing">Missing fields</div>}
                      <div className="ce-tooltip-hint">Click to edit</div>
                    </div>
                  </Tooltip>
                </Polyline>
                {gate.positions.map((pos, idx) => (
                  <CircleMarker
                    key={`${gate.id}-${idx}`}
                    center={pos}
                    radius={isSelected ? 7 : 5}
                    pathOptions={{
                      fillColor: color,
                      fillOpacity: 0.9,
                      color: '#fff',
                      weight: isSelected ? 2 : 1,
                    }}
                    eventHandlers={{
                      click: () => onSelectGate(gate.id),
                      mouseover: () => setHoveredGate(gate.id),
                      mouseout: () => setHoveredGate(null),
                    }}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>
      <div className="ce-gate-browser-legend">
        <span className="ce-legend-item"><span className="ce-legend-swatch" style={{background:'#4CAF50'}} /> Complete</span>
        <span className="ce-legend-item"><span className="ce-legend-swatch" style={{background:'#ff9800'}} /> Missing fields</span>
        <span className="ce-legend-item"><span className="ce-legend-swatch" style={{background:'#2196F3'}} /> Selected</span>
      </div>
    </div>
  );
}

// ─── Main Collection Editor ──────────────────────────────────────────────────

const CollectionEditor = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [locations, setLocations] = useState([]);
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Location state
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [newLocationId, setNewLocationId] = useState('');
  const [creatingNewLocation, setCreatingNewLocation] = useState(false);
  const [locationData, setLocationData] = useState({ ...DEFAULT_LOCATION });
  const [locationDirty, setLocationDirty] = useState(false);

  // Gate state
  const [selectedGateId, setSelectedGateId] = useState('');
  const [newGateId, setNewGateId] = useState('');
  const [creatingNewGate, setCreatingNewGate] = useState(false);
  const [gateData, setGateData] = useState({ ...DEFAULT_GATE });
  const [gateDirty, setGateDirty] = useState(false);

  // Trail state
  const [trails, setTrails] = useState([]);
  const [selectedTrailId, setSelectedTrailId] = useState('');
  const [newTrailId, setNewTrailId] = useState('');
  const [creatingNewTrail, setCreatingNewTrail] = useState(false);
  const [trailData, setTrailData] = useState({ ...DEFAULT_TRAIL });
  const [trailDirty, setTrailDirty] = useState(false);

  // Admin user state
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedAdminUserId, setSelectedAdminUserId] = useState('');
  const [newAdminUserId, setNewAdminUserId] = useState('');
  const [creatingNewAdminUser, setCreatingNewAdminUser] = useState(false);
  const [adminUserData, setAdminUserData] = useState({ ...DEFAULT_ADMIN_USER });
  const [adminUserDirty, setAdminUserDirty] = useState(false);

  // Location filters for gates, trails, and admin users
  const [gateLocationFilter, setGateLocationFilter] = useState('');
  const [trailLocationFilter, setTrailLocationFilter] = useState('');
  const [adminUserLocationFilter, setAdminUserLocationFilter] = useState('');
  const [adminUserSearchQuery, setAdminUserSearchQuery] = useState('');

  // Load all collections on mount — use allSettled so one denied collection
  // doesn't block the others (e.g. users_admin_app may need Firestore rules).
  const loadCollections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        getDocs(collection(db, 'locations')),
        getDocs(collection(db, 'gates')),
        getDocs(collection(db, 'trails')),
        getDocs(collection(db, 'users_admin_app')),
      ]);

      const extract = (r) => r.status === 'fulfilled' ? r.value.docs.map(d => ({ id: d.id, data: d.data() })) : [];
      setLocations(extract(results[0]));
      setGates(extract(results[1]));
      setTrails(extract(results[2]));
      setAdminUsers(extract(results[3]));

      const failed = ['locations', 'gates', 'trails', 'users_admin_app']
        .filter((_, i) => results[i].status === 'rejected');
      if (failed.length > 0) {
        setError(`Permission denied on: ${failed.join(', ')}. Update Firestore rules to allow access.`);
      }
    } catch (err) {
      setError('Failed to load collections: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  // Clear success message after 3s
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // ── Location handlers ────────────────────────────────────────

  const handleSelectLocation = async (id) => {
    if (!id) {
      setSelectedLocationId('');
      setLocationData({ ...DEFAULT_LOCATION });
      setLocationDirty(false);
      setCreatingNewLocation(false);
      return;
    }
    setCreatingNewLocation(false);
    setSelectedLocationId(id);
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'locations', id));
      if (snap.exists()) {
        const d = snap.data();
        setLocationData({
          name: d.name || '',
          location_name: d.location_name || '',
          city: d.city || '',
          state: d.state || '',
          coordinates: d.coordinates || { lat: d.lat || 0, lon: d.lng || d.lon || 0, zoom: d.zoom || 15 },
          ops_time_start: d.ops_time_start || '',
          ops_time_end: d.ops_time_end || '',
          parking: d.parking || [],
          images: d.images || [],
          logo: d.logo || '',
          admin_app: d.admin_app || { status: 'free', stripe_customer_id: '', subscription_id: '', current_period_end: '' },
          revenue_share_perc: d.revenue_share_perc ?? 0.09,
        });
      } else {
        setLocationData({ ...DEFAULT_LOCATION });
      }
      setLocationDirty(false);
    } catch (err) {
      setError('Failed to load location: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = useCallback((update) => {
    if (typeof update === 'function') {
      setLocationData(prev => {
        const next = update(prev);
        return next;
      });
    } else {
      setLocationData(update);
    }
    setLocationDirty(true);
  }, []);

  const handleStartNewLocation = () => {
    setCreatingNewLocation(true);
    setSelectedLocationId('');
    setNewLocationId('');
    setLocationData({ ...DEFAULT_LOCATION });
    setLocationDirty(true);
  };

  const handleSaveLocation = async () => {
    const customId = creatingNewLocation ? newLocationId.trim() : null;
    const existingId = selectedLocationId;
    setSaving(true);
    setError(null);
    try {
      const writeData = { ...locationData };
      if (writeData.name && !writeData.location_name) writeData.location_name = writeData.name;
      if (writeData.location_name && !writeData.name) writeData.name = writeData.location_name;
      const adminApp = { ...writeData.admin_app };
      Object.keys(adminApp).forEach(k => { if (adminApp[k] === '') delete adminApp[k]; });
      if (!adminApp.status) adminApp.status = 'free';
      writeData.admin_app = adminApp;

      let savedId;
      if (creatingNewLocation && !customId) {
        const docRef = await addDoc(collection(db, 'locations'), writeData);
        savedId = docRef.id;
      } else {
        savedId = customId || existingId;
        await setDoc(doc(db, 'locations', savedId), writeData, { merge: true });
      }
      setSuccess(`Location "${savedId}" saved successfully`);
      setLocationDirty(false);
      if (creatingNewLocation) {
        setCreatingNewLocation(false);
        setSelectedLocationId(savedId);
      }
      await loadCollections();
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Gate handlers ────────────────────────────────────────────

  const handleSelectGate = async (id) => {
    if (!id) {
      setSelectedGateId('');
      setGateData({ ...DEFAULT_GATE });
      setGateDirty(false);
      setCreatingNewGate(false);
      return;
    }
    setCreatingNewGate(false);
    setSelectedGateId(id);
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'gates', id));
      if (snap.exists()) {
        const d = snap.data();
        setGateData({
          coordinates: d.coordinates || DEFAULT_GATE.coordinates,
          trigger_polarity: d.trigger_polarity ?? true,
          location_id: d.location_id || '',
          location_name: d.location_name || '',
          trail_name: d.trail_name || '',
          trail_id: d.trail_id || '',
          placement: d.placement || '',
          userId: d.userId || '',
          createdAt: d.createdAt || '',
        });
      } else {
        setGateData({ ...DEFAULT_GATE });
      }
      setGateDirty(false);
    } catch (err) {
      setError('Failed to load gate: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGateChange = useCallback((update) => {
    if (typeof update === 'function') {
      setGateData(prev => update(prev));
    } else {
      setGateData(update);
    }
    setGateDirty(true);
  }, []);

  const handleStartNewGate = () => {
    setCreatingNewGate(true);
    setSelectedGateId('');
    setNewGateId('');
    setGateData({ ...DEFAULT_GATE });
    setGateDirty(true);
  };

  const handleSaveGate = async () => {
    const customId = creatingNewGate ? newGateId.trim() : null;
    const existingId = selectedGateId;
    setSaving(true);
    setError(null);
    try {
      const writeData = { ...gateData };
      delete writeData.userId;
      delete writeData.createdAt;

      let savedId;
      if (creatingNewGate && !customId) {
        const docRef = await addDoc(collection(db, 'gates'), writeData);
        savedId = docRef.id;
      } else {
        savedId = customId || existingId;
        await setDoc(doc(db, 'gates', savedId), writeData, { merge: true });
      }
      setSuccess(`Gate "${savedId}" saved successfully`);
      setGateDirty(false);
      if (creatingNewGate) {
        setCreatingNewGate(false);
        setSelectedGateId(savedId);
      }
      await loadCollections();
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Trail handlers ───────────────────────────────────────────

  const handleSelectTrail = async (id) => {
    if (!id) {
      setSelectedTrailId('');
      setTrailData({ ...DEFAULT_TRAIL });
      setTrailDirty(false);
      setCreatingNewTrail(false);
      return;
    }
    setCreatingNewTrail(false);
    setSelectedTrailId(id);
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'trails', id));
      if (snap.exists()) {
        const d = snap.data();
        setTrailData({
          trail_name: d.trail_name || d.name || '',
          name: d.name || d.trail_name || '',
          location_id: d.location_id || d.locationId || '',
          difficulty: d.difficulty || 'Beginner',
        });
      } else {
        setTrailData({ ...DEFAULT_TRAIL });
      }
      setTrailDirty(false);
    } catch (err) {
      setError('Failed to load trail: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTrailChange = useCallback((update) => {
    if (typeof update === 'function') {
      setTrailData(prev => update(prev));
    } else {
      setTrailData(update);
    }
    setTrailDirty(true);
  }, []);

  const handleStartNewTrail = () => {
    setCreatingNewTrail(true);
    setSelectedTrailId('');
    setNewTrailId('');
    setTrailData({ ...DEFAULT_TRAIL });
    setTrailDirty(true);
  };

  const handleSaveTrail = async () => {
    const customId = creatingNewTrail ? newTrailId.trim() : null;
    const existingId = selectedTrailId;
    setSaving(true);
    setError(null);
    try {
      const writeData = { ...trailData };
      if (writeData.trail_name && !writeData.name) writeData.name = writeData.trail_name;
      if (writeData.name && !writeData.trail_name) writeData.trail_name = writeData.name;

      let savedId;
      if (creatingNewTrail && !customId) {
        const docRef = await addDoc(collection(db, 'trails'), writeData);
        savedId = docRef.id;
      } else {
        savedId = customId || existingId;
        await setDoc(doc(db, 'trails', savedId), writeData, { merge: true });
      }
      setSuccess(`Trail "${savedId}" saved successfully`);
      setTrailDirty(false);
      if (creatingNewTrail) {
        setCreatingNewTrail(false);
        setSelectedTrailId(savedId);
      }
      await loadCollections();
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Admin user handlers ──────────────────────────────────────

  const handleSelectAdminUser = async (id) => {
    if (!id) {
      setSelectedAdminUserId('');
      setAdminUserData({ ...DEFAULT_ADMIN_USER });
      setAdminUserDirty(false);
      setCreatingNewAdminUser(false);
      return;
    }
    setCreatingNewAdminUser(false);
    setSelectedAdminUserId(id);
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users_admin_app', id));
      if (snap.exists()) {
        const d = snap.data();
        setAdminUserData({
          clerk_user_id: d.clerk_user_id || id,
          email: d.email || '',
          name: d.name || '',
          status: d.status || 'pending',
          default_location_id: d.default_location_id || '',
          locations: (d.locations || []).map(entry => ({
            location_id: entry.location_id || '',
            role: entry.role || 'viewer',
            status: entry.status || 'active',
            created_at: entry.created_at || '',
          })),
          created_at: d.created_at || '',
          updated_at: d.updated_at || '',
        });
      } else {
        setAdminUserData({ ...DEFAULT_ADMIN_USER, clerk_user_id: id });
      }
      setAdminUserDirty(false);
    } catch (err) {
      setError('Failed to load admin user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminUserChange = useCallback((update) => {
    if (typeof update === 'function') {
      setAdminUserData(prev => update(prev));
    } else {
      setAdminUserData(update);
    }
    setAdminUserDirty(true);
  }, []);

  const handleStartNewAdminUser = () => {
    setCreatingNewAdminUser(true);
    setSelectedAdminUserId('');
    setNewAdminUserId('');
    setAdminUserData({ ...DEFAULT_ADMIN_USER });
    setAdminUserDirty(true);
  };

  const handleSaveAdminUser = async () => {
    const customId = creatingNewAdminUser ? newAdminUserId.trim() : null;
    const existingId = selectedAdminUserId;

    if (creatingNewAdminUser && !customId) {
      setError('Clerk user ID is required for new admin users.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const savedId = customId || existingId;
      const writeData = {
        clerk_user_id: savedId,
        email: adminUserData.email || '',
        name: adminUserData.name || '',
        status: adminUserData.status || 'pending',
        default_location_id: adminUserData.default_location_id || null,
        locations: (adminUserData.locations || [])
          .filter(entry => entry.location_id)
          .map(entry => ({
            location_id: entry.location_id,
            role: entry.role || 'viewer',
            status: entry.status || 'active',
            created_at: parseFirestoreTimestamp(entry.created_at) || new Date(),
          })),
        updated_at: serverTimestamp(),
      };

      if (creatingNewAdminUser) {
        writeData.created_at = serverTimestamp();
      }

      await setDoc(doc(db, 'users_admin_app', savedId), writeData, { merge: true });

      setSuccess(`Admin user "${savedId}" saved successfully`);
      setAdminUserDirty(false);
      if (creatingNewAdminUser) {
        setCreatingNewAdminUser(false);
        setSelectedAdminUserId(savedId);
      }
      await loadCollections();
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Discard handlers
  const handleDiscardLocation = () => {
    if (creatingNewLocation) {
      setCreatingNewLocation(false);
      setNewLocationId('');
      setLocationData({ ...DEFAULT_LOCATION });
    } else if (selectedLocationId) {
      handleSelectLocation(selectedLocationId);
    }
    setLocationDirty(false);
  };

  const handleDiscardGate = () => {
    if (creatingNewGate) {
      setCreatingNewGate(false);
      setNewGateId('');
      setGateData({ ...DEFAULT_GATE });
    } else if (selectedGateId) {
      handleSelectGate(selectedGateId);
    }
    setGateDirty(false);
  };

  const handleDiscardTrail = () => {
    if (creatingNewTrail) {
      setCreatingNewTrail(false);
      setNewTrailId('');
      setTrailData({ ...DEFAULT_TRAIL });
    } else if (selectedTrailId) {
      handleSelectTrail(selectedTrailId);
    }
    setTrailDirty(false);
  };

  const handleDiscardAdminUser = () => {
    if (creatingNewAdminUser) {
      setCreatingNewAdminUser(false);
      setNewAdminUserId('');
      setAdminUserData({ ...DEFAULT_ADMIN_USER });
    } else if (selectedAdminUserId) {
      handleSelectAdminUser(selectedAdminUserId);
    }
    setAdminUserDirty(false);
  };

  // Filtered lists
  const filteredGates = gateLocationFilter
    ? gates.filter(g => g.data.location_id === gateLocationFilter)
    : gates;

  const filteredTrails = trailLocationFilter
    ? trails.filter(t => (t.data.location_id || t.data.locationId) === trailLocationFilter)
    : trails;

  const filteredAdminUsers = useMemo(() => {
    let result = adminUsers;
    if (adminUserLocationFilter) {
      result = result.filter(u =>
        (u.data.locations || []).some(entry => entry.location_id === adminUserLocationFilter)
      );
    }
    if (adminUserSearchQuery.trim()) {
      const q = adminUserSearchQuery.trim().toLowerCase();
      result = result.filter(u =>
        (u.data.email || '').toLowerCase().includes(q) ||
        (u.data.name || '').toLowerCase().includes(q) ||
        u.id.toLowerCase().includes(q) ||
        (u.data.status || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [adminUsers, adminUserLocationFilter, adminUserSearchQuery]);

  const showLocationForm = creatingNewLocation || selectedLocationId;
  const showGateForm = creatingNewGate || selectedGateId;
  const showTrailForm = creatingNewTrail || selectedTrailId;
  const showAdminUserForm = creatingNewAdminUser || selectedAdminUserId;
  const displayLocId = creatingNewLocation ? (newLocationId || '(auto-generated)') : selectedLocationId;
  const displayGateId = creatingNewGate ? (newGateId || '(auto-generated)') : selectedGateId;
  const displayTrailId = creatingNewTrail ? (newTrailId || '(auto-generated)') : selectedTrailId;
  const displayAdminUserId = creatingNewAdminUser ? (newAdminUserId || '(Clerk user ID required)') : selectedAdminUserId;

  return (
    <div className="ce-page">
      {/* Tab Bar */}
      <div className="ce-tab-bar">
        <button className={`ce-tab ${activeTab === 0 ? 'active' : ''}`} onClick={() => setActiveTab(0)}>
          <MapPin size={14} /> Locations
        </button>
        <button className={`ce-tab ${activeTab === 1 ? 'active' : ''}`} onClick={() => setActiveTab(1)}>
          <Navigation size={14} /> Gates
        </button>
        <button className={`ce-tab ${activeTab === 2 ? 'active' : ''}`} onClick={() => setActiveTab(2)}>
          <Milestone size={14} /> Trails
        </button>
        <button className={`ce-tab ${activeTab === 3 ? 'active' : ''}`} onClick={() => setActiveTab(3)}>
          <UserCircle size={14} /> Admin Users
        </button>
      </div>

      {/* Status bar */}
      {(error || success) && (
        <div className={`ce-status ${error ? 'ce-status-error' : 'ce-status-success'}`}>
          {error ? <AlertCircle size={14} /> : <Check size={14} />}
          <span>{error || success}</span>
          {error && <button className="ce-status-dismiss" onClick={() => setError(null)}>&times;</button>}
        </div>
      )}

      {/* Locations Tab */}
      {activeTab === 0 && (
        <div className="ce-panel">
          <div className="ce-toolbar">
            <div className="ce-selector">
              <label>Location</label>
              {creatingNewLocation ? (
                <div className="ce-new-id-row">
                  <span className="ce-new-label">New Location</span>
                  <button className="ce-btn-sm ce-btn-cancel" onClick={() => { setCreatingNewLocation(false); setSelectedLocationId(''); setLocationDirty(false); }}>Cancel</button>
                </div>
              ) : (
                <select
                  value={selectedLocationId}
                  onChange={e => handleSelectLocation(e.target.value)}
                  className="ce-input"
                >
                  <option value="">— select a location —</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.id} — {loc.data.name || loc.data.location_name || '(unnamed)'}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="ce-toolbar-actions">
              <button className="ce-btn-sm" onClick={handleStartNewLocation}>
                <Plus size={14} /> New
              </button>
              <button className="ce-btn-sm" onClick={loadCollections} disabled={loading}>
                <Search size={14} /> Refresh
              </button>
              {locationDirty && (
                <button className="ce-btn-sm ce-btn-discard" onClick={handleDiscardLocation}>
                  <X size={14} /> Discard
                </button>
              )}
              <button
                className="ce-btn-sm ce-btn-save"
                onClick={handleSaveLocation}
                disabled={saving || !locationDirty}
              >
                {saving ? <Loader2 size={14} className="ce-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
          </div>

          {loading && !saving ? (
            <div className="ce-loading"><Loader2 size={20} className="ce-spin" /> Loading...</div>
          ) : showLocationForm ? (
            <LocationEditor
              locationId={displayLocId}
              data={locationData}
              onChange={handleLocationChange}
              locations={locations}
              isNew={creatingNewLocation}
              customId={newLocationId}
              onCustomIdChange={setNewLocationId}
            />
          ) : (
            <div className="ce-empty">
              <MapPin size={32} />
              <p>Select a location to edit or create a new one.</p>
              <span className="ce-hint">{locations.length} location{locations.length !== 1 ? 's' : ''} loaded</span>
            </div>
          )}
        </div>
      )}

      {/* Gates Tab */}
      {activeTab === 1 && (
        <div className="ce-panel ce-panel-gates">
          <div className="ce-toolbar">
            <div className="ce-toolbar-filters">
              <div className="ce-selector">
                <label>Location</label>
                <select value={gateLocationFilter} onChange={e => { setGateLocationFilter(e.target.value); setSelectedGateId(''); setCreatingNewGate(false); }} className="ce-input">
                  <option value="">All locations ({gates.length} gates)</option>
                  {[...locations].sort((a, b) => (a.data.name || a.data.location_name || '').localeCompare(b.data.name || b.data.location_name || '')).map(loc => {
                    const count = gates.filter(g => g.data.location_id === loc.id).length;
                    return (
                      <option key={loc.id} value={loc.id}>
                        {loc.data.name || loc.data.location_name || loc.id} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="ce-selector">
                <label>Gate</label>
                {creatingNewGate ? (
                  <div className="ce-new-id-row">
                    <span className="ce-new-label">New Gate</span>
                    <button className="ce-btn-sm ce-btn-cancel" onClick={() => { setCreatingNewGate(false); setSelectedGateId(''); setGateDirty(false); }}>Cancel</button>
                  </div>
                ) : (
                  <select
                    value={selectedGateId}
                    onChange={e => handleSelectGate(e.target.value)}
                    className="ce-input"
                  >
                    <option value="">— select from list (or click map) —</option>
                    {filteredGates.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.data.trail_name || g.id}{g.data.placement ? ` (${g.data.placement})` : ''}{!g.data.trail_name ? '' : ` — ${g.id}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="ce-toolbar-actions">
              <button className="ce-btn-sm" onClick={handleStartNewGate}>
                <Plus size={14} /> New
              </button>
              <button className="ce-btn-sm" onClick={loadCollections} disabled={loading}>
                <Search size={14} /> Refresh
              </button>
              {gateDirty && (
                <button className="ce-btn-sm ce-btn-discard" onClick={handleDiscardGate}>
                  <X size={14} /> Discard
                </button>
              )}
              <button
                className="ce-btn-sm ce-btn-save"
                onClick={handleSaveGate}
                disabled={saving || !gateDirty}
              >
                {saving ? <Loader2 size={14} className="ce-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
          </div>

          {loading && !saving ? (
            <div className="ce-loading"><Loader2 size={20} className="ce-spin" /> Loading...</div>
          ) : (
            <div className="ce-gates-split">
              <GateMapBrowser
                gates={gates}
                locations={locations}
                selectedGateId={selectedGateId}
                onSelectGate={handleSelectGate}
                locationFilter={gateLocationFilter}
              />
              {showGateForm ? (
                <div className="ce-gates-editor">
                  <GateEditor
                    gateId={displayGateId}
                    data={gateData}
                    onChange={handleGateChange}
                    locations={locations}
                    isNew={creatingNewGate}
                    customId={newGateId}
                    onCustomIdChange={setNewGateId}
                  />
                </div>
              ) : (
                <div className="ce-gates-editor ce-gates-editor-empty">
                  <Navigation size={28} />
                  <p>Click a gate on the map or select from the dropdown to edit fields.</p>
                  <span className="ce-hint">
                    Orange lines = missing fields. Green = complete.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Trails Tab */}
      {activeTab === 2 && (
        <div className="ce-panel">
          <div className="ce-toolbar">
            <div className="ce-toolbar-filters">
              <div className="ce-selector">
                <label>Location</label>
                <select value={trailLocationFilter} onChange={e => { setTrailLocationFilter(e.target.value); setSelectedTrailId(''); }} className="ce-input">
                  <option value="">All locations ({trails.length} trails)</option>
                  {locations.map(loc => {
                    const count = trails.filter(t => (t.data.location_id || t.data.locationId) === loc.id).length;
                    return (
                      <option key={loc.id} value={loc.id}>
                        {loc.data.name || loc.data.location_name || loc.id} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="ce-selector">
                <label>Trail</label>
                {creatingNewTrail ? (
                  <div className="ce-new-id-row">
                    <span className="ce-new-label">New Trail</span>
                    <button className="ce-btn-sm ce-btn-cancel" onClick={() => { setCreatingNewTrail(false); setSelectedTrailId(''); setTrailDirty(false); }}>Cancel</button>
                  </div>
                ) : (
                  <select
                    value={selectedTrailId}
                    onChange={e => handleSelectTrail(e.target.value)}
                    className="ce-input"
                  >
                    <option value="">— select a trail —</option>
                    {filteredTrails.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.id} — {t.data.trail_name || t.data.name || '(unnamed)'}{t.data.difficulty ? ` [${t.data.difficulty}]` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="ce-toolbar-actions">
              <button className="ce-btn-sm" onClick={handleStartNewTrail}>
                <Plus size={14} /> New
              </button>
              <button className="ce-btn-sm" onClick={loadCollections} disabled={loading}>
                <Search size={14} /> Refresh
              </button>
              {trailDirty && (
                <button className="ce-btn-sm ce-btn-discard" onClick={handleDiscardTrail}>
                  <X size={14} /> Discard
                </button>
              )}
              <button
                className="ce-btn-sm ce-btn-save"
                onClick={handleSaveTrail}
                disabled={saving || !trailDirty}
              >
                {saving ? <Loader2 size={14} className="ce-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
          </div>

          {loading && !saving ? (
            <div className="ce-loading"><Loader2 size={20} className="ce-spin" /> Loading...</div>
          ) : showTrailForm ? (
            <TrailEditor
              trailId={displayTrailId}
              data={trailData}
              onChange={handleTrailChange}
              locations={locations}
              gates={gates}
              isNew={creatingNewTrail}
              customId={newTrailId}
              onCustomIdChange={setNewTrailId}
            />
          ) : (
            <div className="ce-empty">
              <Milestone size={32} />
              <p>Select a trail to edit or create a new one.</p>
              <span className="ce-hint">{trails.length} trail{trails.length !== 1 ? 's' : ''} loaded</span>
            </div>
          )}
        </div>
      )}

      {/* Admin Users Tab */}
      {activeTab === 3 && (
        <div className="ce-panel">
          <div className="ce-toolbar">
            <div className="ce-toolbar-filters">
              <div className="ce-selector ce-selector-search">
                <label>Search</label>
                <div className="ce-search-input-wrap">
                  <Search size={13} className="ce-search-icon" />
                  <input
                    type="text"
                    value={adminUserSearchQuery}
                    onChange={e => setAdminUserSearchQuery(e.target.value)}
                    className="ce-input ce-input-search"
                    placeholder="Email, name, or Clerk ID…"
                  />
                  {adminUserSearchQuery && (
                    <button
                      className="ce-search-clear"
                      onClick={() => setAdminUserSearchQuery('')}
                      title="Clear search"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
              <div className="ce-selector">
                <label>Location</label>
                <select
                  value={adminUserLocationFilter}
                  onChange={e => { setAdminUserLocationFilter(e.target.value); setSelectedAdminUserId(''); setCreatingNewAdminUser(false); }}
                  className="ce-input"
                >
                  <option value="">All locations ({adminUsers.length} users)</option>
                  {[...locations].sort((a, b) =>
                    (a.data.name || a.data.location_name || '').localeCompare(b.data.name || b.data.location_name || '')
                  ).map(loc => {
                    const count = adminUsers.filter(u =>
                      (u.data.locations || []).some(entry => entry.location_id === loc.id)
                    ).length;
                    return (
                      <option key={loc.id} value={loc.id}>
                        {loc.data.name || loc.data.location_name || loc.id} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="ce-selector">
                <label>Admin User</label>
                {creatingNewAdminUser ? (
                  <div className="ce-new-id-row">
                    <span className="ce-new-label">New Admin User</span>
                    <button
                      className="ce-btn-sm ce-btn-cancel"
                      onClick={() => { setCreatingNewAdminUser(false); setSelectedAdminUserId(''); setAdminUserDirty(false); }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <select
                    value={selectedAdminUserId}
                    onChange={e => handleSelectAdminUser(e.target.value)}
                    className="ce-input"
                  >
                    <option value="">
                      {filteredAdminUsers.length === adminUsers.length
                        ? `— select an admin user (${adminUsers.length}) —`
                        : `— ${filteredAdminUsers.length} of ${adminUsers.length} users —`}
                    </option>
                    {[...filteredAdminUsers].sort((a, b) =>
                      (a.data.email || a.data.name || a.id).localeCompare(b.data.email || b.data.name || b.id)
                    ).map(u => (
                      <option key={u.id} value={u.id}>
                        {u.data.email || u.data.name || u.id} — {u.data.status || 'pending'} ({u.id})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="ce-toolbar-actions">
              <button className="ce-btn-sm" onClick={handleStartNewAdminUser}>
                <Plus size={14} /> New
              </button>
              <button className="ce-btn-sm" onClick={loadCollections} disabled={loading}>
                <Search size={14} /> Refresh
              </button>
              {adminUserDirty && (
                <button className="ce-btn-sm ce-btn-discard" onClick={handleDiscardAdminUser}>
                  <X size={14} /> Discard
                </button>
              )}
              <button
                className="ce-btn-sm ce-btn-save"
                onClick={handleSaveAdminUser}
                disabled={saving || !adminUserDirty}
              >
                {saving ? <Loader2 size={14} className="ce-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
          </div>

          {loading && !saving ? (
            <div className="ce-loading"><Loader2 size={20} className="ce-spin" /> Loading...</div>
          ) : showAdminUserForm ? (
            <AdminUserEditor
              userId={displayAdminUserId}
              data={adminUserData}
              onChange={handleAdminUserChange}
              locations={locations}
              isNew={creatingNewAdminUser}
              customId={newAdminUserId}
              onCustomIdChange={setNewAdminUserId}
            />
          ) : (
            <div className="ce-empty">
              <UserCircle size={32} />
              <p>Select an admin user to edit or create a new one.</p>
              <span className="ce-hint">
                {adminUsers.length} user{adminUsers.length !== 1 ? 's' : ''} loaded — document ID = Clerk user ID
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollectionEditor;
